import React, { useState, useEffect, useRef } from 'react';
import { User, ChevronDown, X } from 'lucide-react';
import { useLocalAuth } from '../hooks/useLocalAuth';

interface TechnicianSelectorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

interface Technician {
  id: string;
  name: string;
  company?: string;
}

export const TechnicianSelector: React.FC<TechnicianSelectorProps> = ({ 
  value, 
  onChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useLocalAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load technicians from localStorage
  useEffect(() => {
    const storedTechnicians = localStorage.getItem('installtrack_technicians');
    if (storedTechnicians) {
      setTechnicians(JSON.parse(storedTechnicians));
    } else if (user) {
      // Initialize with current user if no technicians are stored
      const initialTechnicians = [{
        id: user.id,
        name: user.name,
        company: user.company
      }];
      setTechnicians(initialTechnicians);
      localStorage.setItem('installtrack_technicians', JSON.stringify(initialTechnicians));
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (technicianName: string) => {
    onChange(technicianName);
    setIsOpen(false);
    setSearchTerm('');
  };

  const filteredTechnicians = technicians.filter(tech => 
    tech.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div 
        className="flex items-center justify-between w-full p-2.5 border border-gray-300 rounded-lg cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <User className="h-5 w-5 text-gray-400" />
          <span className="text-gray-700">
            {value || (user ? user.name : 'Techniker auswählen')}
          </span>
        </div>
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="p-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Techniker suchen..."
              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          <ul className="max-h-60 overflow-y-auto py-1">
            {filteredTechnicians.length > 0 ? (
              filteredTechnicians.map(tech => (
                <li 
                  key={tech.id}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                  onClick={() => handleSelect(tech.name)}
                >
                  <div>
                    <div className="font-medium">{tech.name}</div>
                    {tech.company && (
                      <div className="text-xs text-gray-500">{tech.company}</div>
                    )}
                  </div>
                  {value === tech.name && (
                    <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                  )}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500 text-center">
                Keine Techniker gefunden
              </li>
            )}
          </ul>
          
          <div className="border-t border-gray-200 p-2">
            <button
              type="button"
              className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
              onClick={() => {
                if (user) {
                  handleSelect(user.name);
                }
              }}
            >
              Aktuellen Benutzer verwenden ({user?.name})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};